import React from "react";
import { useLang } from "../context/LanguageContext";
import { Clock, Clock3, Info, ArrowRight, MessageCircle, CalendarDays } from "lucide-react";

const Pricing = () => {
  const { t } = useLang();
  const p = t.pricing;

  return (
    <section id="inscripciones" className="relative py-24 md:py-32 bg-[#1D3557] text-white overflow-hidden">
      <div className="blob bg-[#FFD500] w-[500px] h-[500px] -top-40 -right-40 opacity-30" />
      <div className="blob bg-[#1E5FBA] w-[400px] h-[400px] -bottom-20 -left-20 opacity-40" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[#FFD500] mb-4">
            {p.overline}
          </div>
          <h2 className="font-fredoka text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05]">
            {p.title}
          </h2>
          <p className="mt-6 text-lg text-white/80 leading-relaxed">{p.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* LEFT — photo card */}
          <div data-testid="pricing-photo" className="lg:col-span-5 relative rounded-[2rem] overflow-hidden min-h-[380px] md:min-h-[520px] border-4 border-white/10">
            <img
              src="/images/aula5.jpeg"
              alt="Actividades en aula"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1D3557]/95 via-[#1D3557]/30 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
              <div className="inline-flex items-center gap-2 bg-[#FFD500] text-[#1D3557] rounded-full px-4 py-2 mb-4 self-start">
                <CalendarDays className="w-4 h-4" strokeWidth={2.75} />
                <span className="text-xs font-bold uppercase tracking-widest">Visita informativa</span>
              </div>
              <div className="font-fredoka text-2xl md:text-3xl font-semibold leading-tight max-w-sm">
                Ven a conocernos y vive la experiencia STEPS con tu pequeño.
              </div>
            </div>
          </div>

          {/* RIGHT — stacked cards */}
          <div className="lg:col-span-7 flex flex-col gap-6 md:gap-8">
            {/* Horario Regular + Extendido side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* Horario Regular */}
              <div data-testid="pricing-schedule" className="bg-white text-[#1D3557] rounded-[2rem] p-7 md:p-8 border-t-8 border-[#1E5FBA]">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#1E5FBA]/10 grid place-items-center">
                    <Clock className="w-5 h-5 text-[#1E5FBA]" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-fredoka text-lg font-semibold">{p.schedule}</h3>
                </div>
                <ul className="space-y-3">
                  {p.schedules.map((s, i) => (
                    <li key={i} className="flex items-center justify-between gap-3 text-sm border-b border-[#E2E8F0] pb-3 last:border-0 last:pb-0">
                      <span className="font-medium text-[#1D3557]">{s.grade}</span>
                      <span className="font-fredoka text-[#457B9D] whitespace-nowrap">{s.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Horario Extendido */}
              <div data-testid="pricing-extended" className="bg-white text-[#1D3557] rounded-[2rem] p-7 md:p-8 border-t-8 border-[#2E9E4E] relative overflow-hidden">
                <div className="absolute -right-8 -bottom-8 w-36 h-36 rounded-full bg-[#2E9E4E]/10" />
                <div className="relative">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-[#2E9E4E]/15 grid place-items-center">
                      <Clock3 className="w-5 h-5 text-[#1F7437]" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-fredoka text-lg font-semibold">{p.extendedTitle}</h3>
                  </div>
                  <div className="font-fredoka text-3xl md:text-4xl font-bold text-[#1F7437] leading-tight">
                    {p.extendedTime}
                  </div>
                  <p className="mt-4 text-sm text-[#457B9D] leading-relaxed">
                    {p.extendedNote}
                  </p>
                </div>
              </div>
            </div>

            {/* Costos — highlighted CTA card */}
            <div data-testid="pricing-costs" className="bg-[#FFD500] text-[#1D3557] rounded-[2rem] p-8 md:p-10 shadow-[0_30px_60px_rgba(255,213,0,0.25)] relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-white/20" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-[#1D3557] text-white text-[11px] font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
                  Costos
                </div>
                <h3 className="font-fredoka text-2xl md:text-3xl font-semibold leading-tight max-w-xl">
                  {p.costsTitle}
                </h3>
                <p className="mt-4 text-[#1D3557]/85 leading-relaxed max-w-2xl">
                  {p.costsDesc}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href="#contacto"
                    data-testid="pricing-cta"
                    className="inline-flex items-center gap-2 rounded-full bg-[#1D3557] text-white px-6 py-3 font-semibold hover:bg-[#1E5FBA] transition-colors"
                  >
                    {p.cta}
                    <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
                  </a>
                  <a
                    href="https://wa.me/524421182816"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="pricing-whatsapp"
                    className="inline-flex items-center gap-2 rounded-full bg-white text-[#1D3557] px-6 py-3 font-semibold hover:bg-[#FDFBF7] transition-colors border-2 border-[#1D3557]/10"
                  >
                    <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-white/10 border border-white/15 backdrop-blur rounded-[2rem] p-8">
          <div className="flex items-center gap-3 mb-4">
            <CalendarDays className="w-5 h-5 text-[#FFD500]" strokeWidth={2.5} />
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#FFD500]">Info</span>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
            {p.notes.map((n, i) => (
              <li key={i} className="flex items-start gap-3 text-white/85 text-sm leading-relaxed">
                <Info className="w-4 h-4 mt-0.5 text-[#FFD500] shrink-0" strokeWidth={2.5} />
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
